import { createContext, useContext, useMemo, useState } from 'react';

import { getReturnUrl } from 'hb-react/frontoffice/components/payments/PaymentStripe/utils';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { LockScreen } from 'hb-react/shared/components/LockScreen';
import { numberWithCurrency } from 'hb-react/shared/utils/formatNumber';
import { fullName } from 'helpers/name';

const PaymentContext = createContext({
  onSuccess: () => {},
  data: {},
  isLoading: false,
  setLoading: () => {},
  paymentAccountConfig: {},
  paymentData: {},
  billingData: {},
  paymentEndpoint: '',
  amount: 0,
  amountFormatted: '',
  currency: '',
  returnUrl: '',
  hasRecurringItems: false,
  cart: {},
});

export const usePaymentContext = () => useContext(PaymentContext);

export const PaymentProvider = ({ children, onSuccess, data }) => {
  const {
    cart,
    _metadata: { payment_gateway: gateway },
  } = data;

  const { currentUser } = useDefaultAppContexts();

  const [isLoading, setLoading] = useState(false);

  const totalFormatted = numberWithCurrency(cart.total, cart.currency, {
    minimumFractionDigits: Number.isInteger(Number(cart.total)) ? 0 : 2,
    locale: currentUser?.locale || I18n.locale,
    currencyDisplay: 'symbol',
  });

  const value = useMemo(
    () => ({
      paymentAccountConfig: gateway,
      paymentData: { cart_uuid: cart.id },
      billingData: {
        name: fullName(currentUser, cart.guest),
        email: currentUser?.email || cart?.guest?.email,
      },
      paymentEndpoint: Routes.order_management_orders_path(),
      amount: cart.total,
      amountFormatted: totalFormatted,
      currency: cart.currency.code,
      returnUrl: getReturnUrl({ cart_id: cart.id }),
      hasRecurringItems: cart.cart_items.some(({ recurring }) => recurring),
      isLoading,
      setLoading,
      onSuccess,
      cart,
    }),
    [cart, currentUser, gateway, isLoading, onSuccess, totalFormatted],
  );

  return (
    <PaymentContext.Provider value={value}>
      {isLoading && <LockScreen />}
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentContext;
